// process.env vars (PUBLIC_URL, NODE_ENV, and anything starting with REACT_APP_) are injected
// by create-react-app at the time the frontend is built. Because we need different vars for
// different environments, this means we have to build the frontend multiple times, which is
// incredibly time-consuming. This file, along with 01_configure.sh, serves as a workaround.

// inject-index-env generates a short js file that sets window.CONFIG based on the env
// vars. Then we load that file in index.html, before any other code runs. Then in this
// file, the `config` var will be populated from window.CONFIG. That way, we can do the
// frontend build once and quickly generate a version for each environment.

// PUBLIC_URL is a special case, because create-react-app injects it into the bundle at
// many places to identify paths to static assets. to deal with this, we always set PUBLIC_URL
// to a placeholder value, and then replace all instances of it with find/sed (this also
// happens in inject-index.env.sh)
//
// To add a new value to this config:
// 1. Add it to Config below
// 2. Add it to the defaults value below
// 3. If you want it in the various environments (production, qa, devprod, etc), add it to the appropriate .env file
// 4. add it to env.template.js
//
// You can then pass it in to tilt by running gorilla like so:
// REACT_APP_{YOUR_NEW_CONFIG_NAME}=true tilt up -- --fe_env=prod

import {setConfig as setWeaveConfig} from '@wandb/weave/config';

import {keyExistsInQueryString} from './util/query-string';

// Config interface
interface Config {
  AUTH_STUB_JWT: string;
  BACKEND_HOST: string; // NOTE: Don't use this directly. Call backendHost() below.
  ALTERNATE_BACKEND_HOST: string;
  ALTERNATE_BACKEND_PORT: string;
  ANALYTICS_DISABLED: boolean; // used to disable collection of third-party analytics data
  CI: boolean;
  ENABLE_DEBUG_FEATURES: boolean;
  ENVIRONMENT_IS_PRIVATE: boolean;
  ENVIRONMENT_NAME: string;
  HOST: string;
  SENTRY_DSN?: string;
  SENTRY_ENVIRONMENT?: string;
  GITHUB_CLIENT_ID?: string;
  STRIPE_API_KEY?: string;
  MAX_BYTES: number;
  MAX_COMPUTE_HOURS_IN_SECONDS: number;
  MAX_STANDARD_COMPUTE_HOURS_IN_SECONDS: number;
  GIT_TAG: string;
  PUBLIC_URL: string; // NOTE: Don't use this directly. Call urlPrefixed() below.
  WEAVE_BACKEND_HOST: string; // NOTE: Don't use this directly. Call backendWeaveHost() below.
  TRACE_BACKEND_BASE_URL: string; // NOTE: Don't use this directly. Call backendTraceBaseUrl() below.
  WB_AGENT_BASE_URL: string;
  DISABLE_TELEMETRY: boolean; // used to disable collection of local analytics data
  LOCAL_TEST_FIXTURE_SERVER_HOST?: string;
  FE_PERF_LOGGING: boolean;
  DATADOG_DEBUG: boolean;
  ENABLE_SECRET_STORE: boolean;
  // We use any type since it comes from the commandline and can be anything, so using any to force
  // our code to validate what it is before using it. If you are creating values for these, you should
  // always pass in strings.
  SERVER_FLAGS: Record<string, any>;
  BANNERS: Record<string, Record<string, any>>;
  ENABLE_TELEPORT_ADMIN_CHECK: boolean;
}

declare global {
  interface Window {
    CONFIG?: Config;
  }
}

const defaults = {
  AUTH_STUB_JWT: '',
  BACKEND_HOST: '',
  ALTERNATE_BACKEND_HOST: '',
  ALTERNATE_BACKEND_PORT: '',
  ANALYTICS_DISABLED: true,
  ENABLE_DEBUG_FEATURES: false,
  ENVIRONMENT_IS_PRIVATE: false,
  CI: false,
  ENVIRONMENT_NAME: '',
  HOST: '',
  SENTRY_DSN: '',
  SENTRY_ENVIRONMENT: '',
  GITHUB_CLIENT_ID: '',
  STRIPE_API_KEY: '',
  MAX_BYTES: 100000000000, // TODO: remove this once billing for storage is hooked up
  MAX_COMPUTE_HOURS_IN_SECONDS: 900000, // 250 * 60 * 60
  MAX_STANDARD_COMPUTE_HOURS_IN_SECONDS: 18000000, // 5000 * 60 * 60
  GIT_TAG: '',
  PUBLIC_URL: '',
  WEAVE_BACKEND_HOST: '',
  TRACE_BACKEND_BASE_URL: '',
  WB_AGENT_BASE_URL: '',
  DISABLE_TELEMETRY: false,
  LOCAL_TEST_FIXTURE_SERVER_HOST: undefined,
  FE_PERF_LOGGING: false,
  DATADOG_DEBUG: false,
  ENABLE_SECRET_STORE: false,
  SERVER_FLAGS: {},
  BANNERS: {},
  ENABLE_TELEPORT_ADMIN_CHECK: false,
};
const config: Config = Object.assign(
  defaults,
  (typeof window !== 'undefined' && window.CONFIG) ?? {}
);

export default config;

export const envIsProd = config.ENVIRONMENT_NAME === 'production';
export const envIsBeta = config.ENVIRONMENT_NAME === 'beta';
export const envIsDev =
  config.ENVIRONMENT_NAME === 'development' ||
  config.ENVIRONMENT_NAME === 'devprod';

// When running tests manually using `jest`,
// we are not properly propagating the environment variables so `envIsTest` is not set.
// `globalThis.process?.env?.NODE_ENV` is set by `jest` as `'test'` when running tests.
// Note that we MUST use the conditional accesses (`?.`) here, or the app will crash
// when trying to read `window.process.env.NODE_ENV`.
// TODO: We can just use `envIsTest` if we fix the environment propagation issue.
export const envIsTest =
  config.ENVIRONMENT_NAME === 'test' ||
  globalThis.process?.env?.NODE_ENV === `test`;

export const envIsIntegration =
  config.ENVIRONMENT_NAME === 'integration' || config.CI;
export const envIsPublicCloud = !config.ENVIRONMENT_IS_PRIVATE;
// See ../../../docs/deployment.md#deployment-types: "Server" deployments are
// Dedicated Cloud (Managed-Install) and Self-Managed (On-Prem). Both currently
// use the historical ENVIRONMENT_NAME="local" marker.
export const envIsServerDeployment = envIsServerDeploymentComputed();

// To make testing simpler, we use this helper function to allow config modification
function envIsServerDeploymentComputed() {
  return config.ENVIRONMENT_IS_PRIVATE && config.ENVIRONMENT_NAME === 'local';
}

export const urlPrefixed = (path?: string, host: boolean = true) => {
  // Ensures urls are fully qualified and properly formed, especially important
  // for allowing users to run the app under a sub-path, i.e. https://mycompany.com/wandb.
  // This should be used anytime we're calling wandb.open, window.location.href, or fetch(...).
  // We have a custom eslint rule wandb/no-unprefixed-urls that will assert we use this helper.
  let url = new URL(window.location.origin);
  // Never use a prefix if we're not in a server deployment.
  if (envIsServerDeploymentComputed()) {
    try {
      url = config.PUBLIC_URL.startsWith('http')
        ? new URL(config.PUBLIC_URL)
        : new URL(config.PUBLIC_URL, url.href);
      url.hostname = window.location.hostname;
    } catch (error) {
      console.error(error);
    }
  }

  if (path != null) {
    // This logic does the magic joining of a sub-path and path in the app.
    url = new URL(url.href.replace(/\/$/, '') + path);
  } else if (!url.href.endsWith('/')) {
    // Some routers require a trailing / at the root
    url = new URL(url.href + '/');
  }
  if (!host) {
    return url.pathname;
  }
  return url.href;
};

export const backendHost = (path?: string) => {
  // Check for alternate hostname in origin.
  const alternateHostBase = config.ALTERNATE_BACKEND_HOST.replace(
    'https://api.',
    ''
  );
  if (
    alternateHostBase !== '' &&
    window.location.origin.indexOf(alternateHostBase) !== -1
  ) {
    // Yeah I know this is insane. It's really just a single use case thing for one customer.
    // They have two domain names pointing to one instance, and the second one needs a custom port too.
    let host = config.ALTERNATE_BACKEND_HOST;
    if (config.ALTERNATE_BACKEND_PORT) {
      host = `${host}:${config.ALTERNATE_BACKEND_PORT}`;
    }

    return host;
  }

  // Support for sub-path mounting
  if (config.BACKEND_HOST === '' && config.PUBLIC_URL !== '') {
    // The backendHost should never have a trailing slash, unlike urlPrefixed
    return urlPrefixed(path).replace(/\/$/, '');
  }

  return config.BACKEND_HOST + (path ?? '');
};

// Defining this now so we can corral all access to this value from the start.
export const backendWeaveExecutionUrl = (shadow: boolean = false) => {
  if (shadow) {
    return config.WEAVE_BACKEND_HOST + '/shadow_execute';
  }
  return config.WEAVE_BACKEND_HOST + '/execute';
};

export const backendWeaveOpsUrl = () => {
  return config.WEAVE_BACKEND_HOST + '/ops';
};

export const backendWeaveViewerUrl = () => {
  return config.WEAVE_BACKEND_HOST + '/wb_viewer';
};

export const backendWeaveHost = () => {
  return config.WEAVE_BACKEND_HOST;
};

export const backendTraceBaseUrl = () => {
  return config.TRACE_BACKEND_BASE_URL;
};

export const hasTraceBackend = backendTraceBaseUrl() !== '';

export const SERVER_FLAGS = {
  enableBackendGrouping: 'ENABLE_BACKEND_GROUPING',
  enableCoreWeaveObservability: 'ENABLE_CORE_WEAVE_OBSERVABILITY',
  enableSemanticLegend: 'ENABLE_SEMANTIC_LEGEND',
  enableWorkspaceTemplates: 'ENABLE_WORKSPACE_TEMPLATES',
  maxRunsLimit: 'MAX_RUNS_LIMIT',
  noNonMonotonicFallback: 'NO_NON_MONOTONIC_FALLBACK',
  systemMetricsQueryLimit: 'SYSTEM_METRICS_QUERY_LIMIT',
  weave1Percentage: 'WEAVE_1_PERCENTAGE',
  weaveShadowPercentage: 'WEAVE_SHADOW_PERCENTAGE',
  windowingDefaultOff: 'WINDOWING_DEFAULT_OFF',
} as const;

type ServerFlagName = (typeof SERVER_FLAGS)[keyof typeof SERVER_FLAGS];

/**
 * Checks if a server flag exists in the configuration.
 *
 * @param flagName - The name of the server flag to check
 * @returns True if the flag is set (regardless of its value), false otherwise
 */
export const hasServerFlag = (flagName: ServerFlagName) => {
  return config.SERVER_FLAGS?.[flagName] != null;
};

/**
 * Retrieves a boolean server flag value from the configuration.
 *
 * @param flagName - The name of the server flag to retrieve (from SERVER_FLAGS constant)
 * @param defaultVal - The default value to return if the flag is not set or invalid
 * @returns The boolean value of the flag, or the default if the flag is not a valid string.
 *          The comparison is case-insensitive (e.g., "TRUE", "true", "True" all return true)
 *
 * @example
 * // Returns true if DYNAMIC_BIN_SIZING is set to "true", false otherwise
 * const isDynamicBinSizing = getServerFlagBool(SERVER_FLAGS.dynamicBinSizing, false);
 *
 * @example
 * // Returns default value if flag is not set
 * const isEnabled = getServerFlagBool(SERVER_FLAGS.someFeature, true); // returns true if flag not set
 */
export const getServerFlagBool = (
  flagName: ServerFlagName,
  defaultVal: boolean
) => {
  const flagVal = config.SERVER_FLAGS?.[flagName];
  if (typeof flagVal !== 'string') {
    return defaultVal;
  }
  return flagVal.toLowerCase() === 'true';
};

/**
 * Retrieves an integer server flag value with bounds validation.
 *
 * @param flagName - The name of the server flag to retrieve
 * @param defaultVal - The default value to return if the flag is invalid or out of bounds
 * @param min - The minimum allowed value (inclusive)
 * @param max - The maximum allowed value (inclusive)
 * @returns The parsed integer value if valid and within bounds, otherwise the default value
 */
export const getServerFlagInt = (
  flagName: ServerFlagName,
  defaultVal: number,
  min: number,
  max: number
) => {
  const flagVal = config.SERVER_FLAGS?.[flagName] ?? defaultVal.toString();
  if (typeof flagVal !== 'string') {
    return defaultVal;
  }
  const parsedVal = parseInt(flagVal, 10);
  if (isNaN(parsedVal) || parsedVal < min || parsedVal > max) {
    return defaultVal;
  }
  return parsedVal;
};

/**
 * @description Returns the boolean value of the server flag WINDOWING_DEFAULT_OFF.
 * @see {@link getServerFlagBool}
 */
export const serverFlagWindowingDefaultOff = () =>
  getServerFlagBool(SERVER_FLAGS.windowingDefaultOff, true);

/**
 * @description Returns the boolean value of the server flag NO_NON_MONOTONIC_FALLBACK.
 * @see {@link getServerFlagBool}
 */
export const serverFlagNoNonMonotonicFallback = () => {
  // Force Saas environments to true
  // this means the environment support non-monotonic x-axis value in FF queries
  if (envIsPublicCloud) {
    return true;
  }
  return getServerFlagBool(SERVER_FLAGS.noNonMonotonicFallback, false);
};

/**
 * @description Returns the integer value of the server flag MAX_RUNS_LIMIT.
 * Controls how many runs you can implement in the line plots in Full Fidelity.
 * @see {@link getServerFlagInt}
 */
export const serverFlagMaxRunsLimit = () =>
  getServerFlagInt(SERVER_FLAGS.maxRunsLimit, 50, 50, 200);

/**
 * @description Returns the integer value of the server flag SYSTEM_METRICS_QUERY_LIMIT.
 * The maximum limit for a system metrics panel before it requires user action to load calculated as numMetrics * numRuns.
 * @see {@link getServerFlagInt}
 */
export const serverFlagSystemMetricsQueryLimit = () =>
  getServerFlagInt(SERVER_FLAGS.systemMetricsQueryLimit, 100, 10, Infinity);

/**
 * @description Returns the integer value of the server flag WEAVE_SHADOW_PERCENTAGE.
 * @see {@link getServerFlagInt}
 */
export const serverWeaveShadowPercentage = () =>
  getServerFlagInt(SERVER_FLAGS.weaveShadowPercentage, 0, 0, 100);

/**
 * @description Returns the integer value of the server flag WEAVE_1_PERCENTAGE.
 * @see {@link getServerFlagInt}
 */
export const serverWeave1Percentage = () =>
  getServerFlagInt(SERVER_FLAGS.weave1Percentage, 0, 0, 100);

/**
 * @description Returns the boolean value of the server flag ENABLE_BACKEND_GROUPING.
 * @see {@link getServerFlagBool}
 */
export const serverEnableBackendGrouping = () =>
  getServerFlagBool(SERVER_FLAGS.enableBackendGrouping, true);

/**
 * @description Returns the boolean value of the server flag ENABLE_CORE_WEAVE_OBSERVABILITY.
 * @see {@link getServerFlagBool}
 */
export const serverEnableCoreWeaveObservability = () =>
  getServerFlagBool(SERVER_FLAGS.enableCoreWeaveObservability, false);

/**
 * @description Returns the boolean value of the server flag ENABLE_SEMANTIC_LEGEND.
 * @see {@link getServerFlagBool}
 */
export const serverEnableSemanticLegend = () =>
  getServerFlagBool(SERVER_FLAGS.enableSemanticLegend, false);

export const frontendPerfLoggingEnabled = () => {
  return config.FE_PERF_LOGGING || keyExistsInQueryString('PERF_LOGGING');
};

setWeaveConfig({
  urlPrefixed,
  backendWeaveExecutionUrl,
  backendWeaveViewerUrl,
  backendWeaveOpsUrl,
  ENABLE_DEBUG_FEATURES: config.ENABLE_DEBUG_FEATURES,
});

export const teleportAdminCheckEnabled = () => {
  return config.ENABLE_TELEPORT_ADMIN_CHECK;
};
